'use client'
import ReactDOM from 'react-dom'

interface PreloadResourcesProps {
  /**
   * Dynamic hero image URL from Prismic (LCP element)
   */
  heroImage?: string
}

/**
 * PreloadResources - Properly preloads critical resources using ReactDOM.preload()
 *
 * This component uses the React 18+ ReactDOM.preload() API to add preload hints
 * to the document head. Calling preload() during render (not in useEffect)
 * ensures the hints are included in the SSR-generated HTML for optimal LCP.
 *
 * Resources preloaded:
 * - /images/hero-bg.svg (static hero background)
 * - Dynamic hero image from Prismic (LCP element)
 */
export default function PreloadResources({ heroImage }: PreloadResourcesProps) {
  // Call preload during render (synchronously) to be hoisted into SSR-generated <head>
  // This is the correct pattern - useEffect would run after hydration and miss initial HTML
  ReactDOM.preload('/images/hero-bg.svg', {
    as: 'image',
    fetchPriority: 'high',
  })

  // Preload dynamic hero image if present (LCP optimization)
  if (heroImage) {
    ReactDOM.preload(heroImage, {
      as: 'image',
      fetchPriority: 'high',
      imageSrcSet: `${heroImage}?w=640 640w, ${heroImage}?w=1024 1024w, ${heroImage}?w=1200 1200w`,
      imageSizes: '(max-width: 640px) 640px, (max-width: 1024px) 1024px, 1200px',
    })
  }

  // This component doesn't render anything
  return null
}
